// Louis Vuitton-inspired Cream Theme for Recruitment CRM
// 🚨 DO NOT apply this theme to CV Builder pages

export const recruitmentTheme = {
  colors: {
    primary: '#f0e4cc',        // Light cream - main brand color
    primaryHover: '#e8d4b0',   // Darker cream for hover states
    accent: '#f5f5dc',         // Cream - accent color
    gold: '#b8860b',           // Gold accent for premium feel
    text: {
      primary: '#8b6914',      // Gold-brown for headers
      secondary: '#b8860b',    // Gold for subheadings
      muted: '#6b7280',        // Gray for body text
      light: '#ffffff',        // White text for dark backgrounds
      dark: '#3a2f1f'          // Dark brown for contrast
    },
    background: {
      primary: '#ffffff',      // Pure white cards/forms
      secondary: '#f9fafb',    // Light gray page background
      section: '#f3f4f6',      // Section backgrounds
      cream: '#fdf6e3',        // Warm cream alternative
      overlay: 'rgba(240, 228, 204, 0.95)' // Cream overlay for modals
    },
    status: {
      success: '#059669',      // Green for completed/hired
      warning: '#d97706',      // Orange for pending/in-progress
      error: '#dc2626',        // Red for rejected/cancelled
      info: '#2563eb',         // Blue for new/interviewing
      pending: '#8b5cf6'       // Purple for screening
    },
    border: {
      light: '#e5e7eb',        // Light borders
      medium: '#d1d5db',       // Medium borders
      dark: '#9ca3af',         // Dark borders
      primary: '#f0e4cc'       // Cream borders for emphasis
    }
  },
  
  components: {
    // Card styles with Louis Vuitton-inspired design
    card: 'bg-white rounded-2xl shadow-lg border border-gray-100 p-6 transition-all duration-200 hover:shadow-xl',
    cardHeader: 'border-b border-gray-100 pb-4 mb-6',
    
    // Button variants with cream theme
    button: {
      primary: 'bg-[#b8860b] hover:bg-[#9a7009] text-white rounded-xl px-6 py-3 font-semibold transition-all duration-200 shadow-lg hover:shadow-xl transform hover:-translate-y-0.5',
      secondary: 'bg-white hover:bg-gray-50 text-[#8b6914] border-2 border-[#f0e4cc] rounded-xl px-6 py-3 font-semibold transition-all duration-200',
      ghost: 'text-[#8b6914] hover:bg-[#f0e4cc] hover:bg-opacity-30 rounded-xl px-4 py-2 transition-all duration-200',
      danger: 'bg-red-600 hover:bg-red-700 text-white rounded-xl px-6 py-3 font-semibold transition-all duration-200'
    },
    
    // Input styles with gold focus
    input: 'rounded-xl border-gray-300 focus:border-[#b8860b] focus:ring-[#b8860b] focus:ring-2 transition-all duration-200 px-4 py-3',
    textarea: 'rounded-xl border-gray-300 focus:border-[#b8860b] focus:ring-[#b8860b] focus:ring-2 transition-all duration-200 px-4 py-3 resize-none',
    select: 'rounded-xl border-gray-300 focus:border-[#b8860b] focus:ring-[#b8860b] focus:ring-2 transition-all duration-200 px-4 py-3',
    
    // Badge styles with cream variations
    badge: {
      base: 'rounded-full px-3 py-1 text-sm font-medium transition-all duration-200',
      primary: 'bg-[#f0e4cc] text-[#8b6914]',
      secondary: 'bg-gray-100 text-gray-800',
      success: 'bg-green-100 text-green-800',
      warning: 'bg-orange-100 text-orange-800',
      error: 'bg-red-100 text-red-800',
      info: 'bg-blue-100 text-blue-800'
    },
    
    // Navigation styles
    nav: {
      sidebar: 'bg-[#f0e4cc] text-black shadow-2xl',
      sidebarItem: 'flex items-center px-6 py-3 text-black hover:bg-[#e8d4b0] transition-all duration-200 rounded-lg mx-2 my-1',
      sidebarItemActive: 'flex items-center px-6 py-3 bg-[#b8860b] text-white rounded-lg mx-2 my-1 shadow-lg',
      header: 'bg-white shadow-lg border-b border-gray-200',
      breadcrumb: 'text-[#8b6914] hover:text-[#b8860b] transition-colors duration-200'
    },
    
    // Status indicators
    status: {
      dot: 'w-3 h-3 rounded-full',
      badge: 'px-3 py-1 rounded-full text-sm font-medium',
      card: 'p-4 rounded-xl border-l-4'
    },
    
    // Modal and overlay styles
    modal: 'bg-white rounded-2xl shadow-2xl max-w-2xl w-full mx-4 p-6',
    overlay: 'fixed inset-0 bg-black bg-opacity-50 backdrop-blur-sm z-50 flex items-center justify-center',
    
    // Table styles
    table: {
      container: 'bg-white rounded-2xl shadow-lg overflow-hidden',
      header: 'bg-gray-50 border-b border-gray-200',
      row: 'border-b border-gray-100 hover:bg-gray-50 transition-colors duration-200',
      cell: 'px-6 py-4 text-sm'
    },
    
    // Form styles
    form: {
      group: 'space-y-2',
      label: 'block text-sm font-semibold text-[#8b6914] mb-2',
      error: 'text-red-600 text-sm mt-1',
      helper: 'text-gray-500 text-sm mt-1'
    }
  },
  
  // Animation classes
  animations: {
    slideIn: 'transform transition-transform duration-300 ease-in-out',
    fadeIn: 'transition-opacity duration-300 ease-in-out',
    scaleIn: 'transform transition-transform duration-200 ease-in-out hover:scale-105',
    bounce: 'animate-bounce',
    pulse: 'animate-pulse'
  },
  
  // Layout utilities
  layout: {
    container: 'max-w-7xl mx-auto px-4 sm:px-6 lg:px-8',
    section: 'py-8',
    grid: 'grid gap-6',
    flex: 'flex items-center justify-between',
    center: 'flex items-center justify-center'
  }
};

// Theme utility functions
export const getStatusColor = (status: string): string => {
  const statusMap: Record<string, string> = {
    // Task statuses
    'PENDING': 'bg-yellow-100 text-yellow-800',
    'IN_PROGRESS': 'bg-blue-100 text-blue-800',
    'COMPLETED': 'bg-green-100 text-green-800',
    'CANCELLED': 'bg-red-100 text-red-800',
    
    // Candidate statuses
    'NEW': 'bg-blue-100 text-blue-800',
    'SCREENING': 'bg-purple-100 text-purple-800',
    'INTERVIEWING': 'bg-orange-100 text-orange-800',
    'OFFER_SENT': 'bg-indigo-100 text-indigo-800',
    'HIRED': 'bg-green-100 text-green-800',
    'REJECTED': 'bg-red-100 text-red-800',
    'ONBOARDING': 'bg-teal-100 text-teal-800'
  };
  
  return statusMap[status.toUpperCase()] || 'bg-gray-100 text-gray-800';
};

export const getPriorityColor = (priority: string): string => {
  const priorityMap: Record<string, string> = {
    'LOW': 'bg-gray-100 text-gray-800',
    'MEDIUM': 'bg-yellow-100 text-yellow-800',
    'HIGH': 'bg-orange-100 text-orange-800',
    'URGENT': 'bg-red-100 text-red-800'
  };
  
  return priorityMap[priority.toUpperCase()] || 'bg-gray-100 text-gray-800';
};

// CSS classes for quick access
export const themeClasses = {
  // Page layouts
  pageContainer: 'min-h-screen bg-gray-50',
  contentContainer: 'max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8',
  
  // Headers
  pageTitle: 'text-3xl font-bold text-[#8b6914] mb-2',
  pageSubtitle: 'text-lg text-gray-600 mb-8',
  sectionTitle: 'text-xl font-semibold text-[#8b6914] mb-4',
  
  // Cards
  card: recruitmentTheme.components.card,
  cardContent: 'space-y-6',
  
  // Buttons
  primaryButton: recruitmentTheme.components.button.primary,
  secondaryButton: recruitmentTheme.components.button.secondary,
  
  // Forms
  formGroup: 'space-y-2',
  label: 'block text-sm font-semibold text-[#8b6914] mb-2',
  input: recruitmentTheme.components.input,
  
  // Tables
  table: 'min-w-full divide-y divide-gray-200',
  tableHeader: 'bg-gray-50',
  tableRow: 'bg-white hover:bg-gray-50 transition-colors duration-200'
};

export default recruitmentTheme;